import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Menu, X, Stethoscope, Shield, UserRound } from "lucide-react";

const NAV = [
  { to: "/doctors/pune", label: "Find Doctor" },
  { to: "/specialties", label: "Specialties" },
  { to: "/cities", label: "Cities" },
  { to: "/list-your-practice", label: "List Your Practice" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-border">
      <div className="mx-auto max-w-[1280px] px-4 lg:px-6 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt={settings.brand_name} className="h-9 w-9 rounded-lg object-cover" />
          ) : (
            <span className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Stethoscope size={20} strokeWidth={2} />
            </span>
          )}
          <span className="font-display text-lg font-bold tracking-tight">
            {settings.brand_name.replace(/Doctor/i, "")}
            <span className="text-primary">{(settings.brand_name.match(/Doctor/i) || ["Doctor"])[0]}</span>
            <span className="text-muted-foreground font-medium text-xs ml-1 tabular">.in</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "text-primary bg-accent" : "text-foreground/80 hover:text-foreground hover:bg-muted"
                }`
              }
              data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user?.role === "admin" ? (
            <>
              <Link to="/admin" className="btn-ghost hidden sm:inline-flex" data-testid="header-admin-dashboard">
                <Shield size={16} /> Admin
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} className="btn-secondary hidden sm:inline-flex" data-testid="header-logout">
                Log out
              </button>
            </>
          ) : user?.role === "patient" ? (
            <>
              <Link to="/patient" className="btn-ghost hidden sm:inline-flex" data-testid="header-patient-dashboard">
                <UserRound size={16} /> My Visits
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} className="btn-secondary hidden sm:inline-flex" data-testid="header-logout">
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/patient/login" className="btn-ghost hidden sm:inline-flex" data-testid="header-patient-login">
                Patient login
              </Link>
              <Link to="/list-your-practice" className="btn-primary hidden sm:inline-flex" data-testid="header-list-practice">
                List Your Practice
              </Link>
            </>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 rounded-lg grid place-items-center hover:bg-muted"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white">
          <div className="px-4 py-3 flex flex-col gap-1" data-testid="mobile-menu-panel">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-[15px] font-medium ${
                    isActive ? "text-primary bg-accent" : "text-foreground/90 hover:bg-muted"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="h-px bg-border my-1" />
            {user?.role === "patient" && (
              <Link to="/patient" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-[15px] font-medium hover:bg-muted">
                My Visits
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-[15px] font-medium hover:bg-muted">
                Admin Dashboard
              </Link>
            )}
            {!user && (
              <Link to="/patient/login" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-[15px] font-medium hover:bg-muted" data-testid="mobile-patient-login">
                Patient login
              </Link>
            )}
            {user ? (
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="btn-secondary mt-2" data-testid="mobile-logout">
                Log out
              </button>
            ) : (
              <Link to="/list-your-practice" onClick={() => setOpen(false)} className="btn-primary mt-2">
                List Your Practice
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
